import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, pairwise, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { EditorEligibilityOperatorEditStore } from './editor-eligibility-operator-edit.store';
import {
  EditorEligibilityOperatorWithRule
} from '../editor-eligibility-operator-with-rule/editor-eligibility-operator-with-rule.interface';
import { EditorEligibilityTreeStore } from '../editor-eligibility-tree/editor-eligibility-tree.store';


@Component({
    selector: 'eas-editor-eligibility-operator-edit',
    templateUrl: './editor-eligibility-operator-edit.component.html',
    styleUrls: ['./editor-eligibility-operator-edit.component.css'],
    standalone: false
})
export class EditorEligibilityOperatorEditComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  private classNameToFormGroupName = [
    { className: 'HasRole', formGroupName: 'hasRole' },
    { className: 'HasStringUserField', formGroupName: 'hasStringUserField' },
    { className: 'HasDiscreteUserField', formGroupName: 'hasDiscreteUserField' },
    { className: 'HasDateUserField', formGroupName: 'hasDateUserField' },
    { className: 'HasRelation', formGroupName: 'hasRelation' },
    { className: 'HasBookingItem', formGroupName: 'hasBookingItem' },
    { className: 'HasBookingOption', formGroupName: 'hasBookingOption' },
    { className: 'HasBookingType', formGroupName: 'hasBookingType' },
    { className: 'HasEvent', formGroupName: 'hasEvent' },
    { className: 'HasEventCount', formGroupName: 'hasEventCount' },
    { className: 'IsRawOperator', formGroupName: 'isRawOperator' },
  ];
  private affirmativeRules = [
    { className: 'HasRole', value: $localize `Has any of these roles` },
    { className: 'HasStringUserField', value: $localize `Has this user field (string)` },
    { className: 'HasDiscreteUserField', value: $localize `Has this user field (discrete)` },
    { className: 'HasDateUserField', value: $localize `Has this user field (date)` },
    { className: 'HasRelation', value: $localize `Has this relation` },
    { className: 'HasBookingItem', value: $localize `Has any of these booking items` },
    { className: 'HasBookingOption', value: $localize `Has any of these booking options` },
    { className: 'HasBookingType', value: $localize `Has any of these booking types` },
    { className: 'HasEvent', value: $localize `Has any of these events` },
    { className: 'HasEventCount', value: $localize `Has this number of events` },
    { className: 'IsRawOperator', value: $localize `Is this operator (experienced users only)` },
  ];
  private negativeRules = [
    { className: 'HasRole', value: $localize `Has none of these roles` },
    { className: 'HasStringUserField', value: $localize `Has not this user field (string)` },
    { className: 'HasDiscreteUserField', value: $localize `Has not this user field (discrete)` },
    { className: 'HasDateUserField', value: $localize `Has not this user field (date)` },
    { className: 'HasRelation', value: $localize `Has not this relation` },
    { className: 'HasBookingItem', value: $localize `Has none of these booking items` },
    { className: 'HasBookingOption', value: $localize `Has none of these booking options` },
    { className: 'HasBookingType', value: $localize `Has none of these booking types` },
    { className: 'HasEvent', value: $localize `Has none of these events` },
    { className: 'HasEventCount', value: $localize `Has not this number of events` },
    { className: 'IsRawOperator', value: $localize `Is not this operator (experienced users only)` },
  ];

  functions = ['and', 'or'];
  rules: { className: string, value: string }[];

  replacingOperatorWithRule: EditorEligibilityOperatorWithRule;
  replacingOperatorWithRuleForm: FormGroup;

  constructor(
    private dialogRef: MatDialogRef<EditorEligibilityOperatorEditComponent>,
    private eligibilityTreeStore: EditorEligibilityTreeStore,
    private operatorEditStore: EditorEligibilityOperatorEditStore
  ) {

  }

  ngOnInit() {
    this.operatorEditStore.replacingOperatorWithRule$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(replacingOperatorWithRule => {
        this.replacingOperatorWithRule = replacingOperatorWithRule;
        if (this.replacingOperatorWithRule !== null) {
          this.rules = this.replacingOperatorWithRule.operator.affirmative
            ? this.affirmativeRules
            : this.negativeRules;
        }
      });

    this.operatorEditStore.replacingOperatorWithRuleForm$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(replacingOperatorWithRuleForm => {
        this.replacingOperatorWithRuleForm = replacingOperatorWithRuleForm;
      });

    this.operatorEditStore.replacingOperatorWithRuleForm$
      .pipe(
        filter(replacingOperatorWithRuleForm => replacingOperatorWithRuleForm !== null),
        switchMap(replacingOperatorWithRuleForm =>
          replacingOperatorWithRuleForm.get('operator').get('affirmative').valueChanges
        ),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        affirmativeFormValue => {
          this.rules = affirmativeFormValue ? this.affirmativeRules : this.negativeRules;
        }
      );

    this.operatorEditStore.replacingOperatorWithRuleForm$
      .pipe(
        filter(replacingOperatorWithRuleForm => replacingOperatorWithRuleForm !== null),
        switchMap(replacingOperatorWithRuleForm =>
          replacingOperatorWithRuleForm.get('rule').get('ruleClassName').valueChanges
        ),
        startWith(null), pairwise(),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        ([ruleClassNameFormValueOld, ruleClassNameFormValueNew]) => {
          if (ruleClassNameFormValueOld !== null) {
            const formGroupNameOld = this.classNameToFormGroupName.find(
              classNameToFormGroupName => classNameToFormGroupName.className === ruleClassNameFormValueOld
            ).formGroupName;
            this.replacingOperatorWithRuleForm.get('rule').get(formGroupNameOld).disable();
          }
          const formGroupNameNew = this.classNameToFormGroupName.find(
            classNameToFormGroupName => classNameToFormGroupName.className === ruleClassNameFormValueNew
          ).formGroupName;
          this.replacingOperatorWithRuleForm.get('rule').get(formGroupNameNew).enable();
        }
      );
  }

  ngOnDestroy() {
    this.operatorEditStore.terminate();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSubmit() {
    this.operatorEditStore.save();
    this.eligibilityTreeStore.stopEditingOperatorWithRule();
    this.dialogRef.close();
  }

  onCancel() {
    this.eligibilityTreeStore.stopEditingOperatorWithRule();
    this.dialogRef.close();
  }

  getFormGroup(key: string): FormGroup {
    return this.replacingOperatorWithRuleForm.get(key) as FormGroup;
  }

  getOperatorFormControl(key: string): FormControl {
    return this.getFormGroup('operator').controls[key] as FormControl;
  }

  getRuleFormGroup(key: string): FormGroup {
    return this.getFormGroup('rule').controls[key] as FormGroup;
  }

  getRuleFormControl(key: string): FormControl {
    return this.getFormGroup('rule').controls[key] as FormControl;
  }
}
